import { Button } from '@ophelos/ui/components/button';
import { CalendarIcon, CheckCircle2, Copy, Lightbulb, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import type { MultibancoReference as MultibancoReferenceType } from '@/lib/payment-api';
import { formatMoneyInLanguage, fromSubunits } from '@/lib/currency';
import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { useTheme } from '@/components/layout/ThemeProvider';
import { ScrollArea } from '@ophelos/ui/components/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from '@/hooks/useTranslation';

interface MultibancoReferenceProps {
  reference: MultibancoReferenceType;
  onClose?: () => void;
}

export function MultibancoReference({ reference, onClose }: MultibancoReferenceProps) {
  const { t, currentLanguage } = useTranslation();
  const { theme } = useTheme();
  const [copiedEntity, setCopiedEntity] = useState(false);
  const [copiedReference, setCopiedReference] = useState(false);
  const isMobile = useIsMobile();

  const logoSrc = theme === 'dark' ? '/assets/multibanco-dark_mode.svg' : '/assets/multibanco-light_mode.svg';

  const copyToClipboard = async (text: string, setCopied: (value: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formattedAmount = formatMoneyInLanguage(fromSubunits(reference.amount_subunits), currentLanguage, reference.currency);

  const formattedExpiry = reference.expires_at
    ? new Date(reference.expires_at).toLocaleDateString(currentLanguage, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="stack max-md:pt-5 py-2 px-3 gap-2 overflow-hidden">
      {/* Header - fixed */}
      <div className="flex items-start gap-2">
        {isMobile && (
          <div className="shrink-0  size-14 rounded-full bg-card border border-border/75 shadow-xl/7 flex items-center justify-center">
            <img src={logoSrc} alt="Multibanco" className="h-8 w-auto" />
          </div>
        )}
        <div className="stack items-start">
          <h3 className="text-xl md:text-2xl font-semibold tracking-tight">{t('multibanco.title')}</h3>
          <p className="max-md:text-sm text-muted-foreground">{t('multibanco.subtitle')}</p>
        </div>
      </div>

      {/* Scrollable content */}
      <ScrollArea className="flex-1 min-h-0 my-2 border border-border/75 rounded-2xl bg-soft">
        <div className="h-full p-4 stack gap-4 ">
          {!isMobile && (
            <div className="mx-auto size-18 rounded-full bg-card border border-border/75 shadow-xl/7 flex items-center justify-center">
              <img src={logoSrc} alt="Multibanco" className="h-10 w-auto" />
            </div>
          )}
          {/* Entity */}
          <div className="stack gap-2">
            <div className="font-medium ">{t('multibanco.entity')}</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-xl bg-card shadow-xs border p-3">
                <div className="text-2xl font-mono font-bold tracking-wider">{reference.entity}</div>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(reference.entity, setCopiedEntity)}
                title={t('multibanco.copyEntity')}
              >
                {copiedEntity ? <CheckCircle2 className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          {/* Reference */}
          <div className="stack gap-2">
            <div className="font-medium ">{t('multibanco.reference')}</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-xl bg-card shadow-xs border p-3">
                <div className="text-2xl font-mono font-bold tracking-wider">{reference.reference}</div>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(reference.reference, setCopiedReference)}
                title={t('multibanco.copyReference')}
              >
                {copiedReference ? <CheckCircle2 className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          {/* Amount */}
          <div className="stack gap-2">
            <div className="font-medium ">{t('multibanco.amount')}</div>
            <div className="rounded-xl bg-card shadow-xs border p-3">
              <div className="text-2xl font-bold">{formattedAmount}</div>
            </div>
          </div>

          {/* Expiry (if provided) */}
          {formattedExpiry && (
            <div className="stack gap-2">
              <div className="font-medium ">{t('multibanco.validUntil')}</div>
              <div className="flex items-center gap-4 rounded-xl bg-muted shadow-xs border px-3 py-2">
                <CalendarIcon className="size-4 text-muted-foreground opacity-75" />
                <div className="text-lg font-medium">{formattedExpiry}</div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <Alert className="bg-amber-50/50 dark:bg-amber-950/50 border-amber-500/30 dark:border-amber-500/30">
            <Lightbulb className="size-4 text-amber-500!" />
            <AlertTitle className="font-medium text-base">{t('multibanco.howToPay')}</AlertTitle>
            <AlertDescription className="">
              <ol className="list-decimal space-y-2 text-base text-foreground pl-4 mt-2">
                <li>{t('multibanco.step1')}</li>
                <li>{t('multibanco.step2')}</li>
                <li>
                  <Trans i18nKey="multibanco.step3Entity" values={{ entity: reference.entity }} components={{ strong: <strong /> }} />
                </li>
                <li>
                  <Trans i18nKey="multibanco.step3Reference" values={{ reference: reference.reference }} components={{ strong: <strong /> }} />
                </li>
                <li>
                  <Trans i18nKey="multibanco.step3Amount" values={{ amount: formattedAmount }} components={{ strong: <strong /> }} />
                </li>
                <li>{t('multibanco.step6')}</li>
              </ol>
              <div className="mt-4 -ml-6 text-base font-medium flex items-start gap-2">
                <TriangleAlert className="size-4 text-amber-500! shrink-0 mt-1" />
                <span>{t('multibanco.onlineBankingNote')}</span>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </ScrollArea>
      {/* Footer - fixed */}
      <div className="flex shrink-0 justify-end gap-4">
        {onClose && (
          <div className="flex justify-center">
            <Button onClick={onClose} variant="outline" size="lg">
              {t('multibanco.close')}
            </Button>
          </div>
        )}
        {/* TODO: Enable this when email sender is implemented */}
        {false && (
          <Button size="lg" variant="secondary">
            {t('multibanco.emailDetails')}
          </Button>
        )}
      </div>
    </div>
  );
}
